import React, { type ReactNode } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

export type PanelEstiloVotoRegistradoProps = {
  /** Igual que en ficha de votación: solapa bajo foto hero. */
  withSolapaFoto?: boolean;
  /** Check verde con borde claro encima del título (p. ej. «¡Ya registramos tu voto!» en `/votar`). */
  iconoCheckCabecera?: boolean;
  /** Si se omite o está vacío, no se muestra el título. */
  title?: string;
  /** Texto junto al icono ℹ️ cuando no se usa `noticeSlot`. */
  noticeText?: string;
  /** Sustituye el contenido del recuadro (p. ej. logotema + leyenda). */
  noticeSlot?: ReactNode;
  /** Contenido dentro del panel marrón, encima del recuadro del aviso / logotema (p. ej. logo y nombre del local). */
  slotEncimaRecuadro?: ReactNode;
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  /** Desactiva el botón principal (p. ej. ubicación no válida en `/r`). */
  primaryActionDisabled?: boolean;
  /**
   * El panel marrón crece hasta ocupar el alto disponible; el recuadro de aviso queda centrado
   * y el botón principal se ancla al fondo (p. ej. pantalla `/r`).
   */
  rellenarAlturaDisponible?: boolean;
};

/**
 * Bloque marrón / aviso en recuadro / botón rojo, misma línea visual que
 * «¡Ya registramos tu voto!» en la pantalla de votación pública.
 */
export function PanelEstiloVotoRegistrado({
  withSolapaFoto,
  iconoCheckCabecera,
  title = '',
  noticeText = '',
  noticeSlot,
  slotEncimaRecuadro,
  primaryActionLabel,
  onPrimaryAction,
  primaryActionDisabled = false,
  rellenarAlturaDisponible = false,
}: PanelEstiloVotoRegistradoProps) {
  const showAction = Boolean(primaryActionLabel && onPrimaryAction);
  const showTitle = Boolean(title?.trim());
  const noticePlain = typeof noticeText === 'string' ? noticeText.trim() : '';
  const hasNotice = Boolean(noticeSlot) || noticePlain.length > 0;
  const hasEncima = Boolean(slotEncimaRecuadro);

  /** Check + título + recuadro de aviso juntos y centrados (p. ej. «ya registrado» en `/votar`). */
  const agruparCabeceraYAvisoCentrados =
    rellenarAlturaDisponible &&
    !hasEncima &&
    hasNotice &&
    (iconoCheckCabecera || showTitle);

  const noticeRecuadro = hasNotice ? (
    <View
      style={[
        styles.yaVotoNoticeRegistrado,
        !showTitle && !hasEncima ? styles.yaVotoNoticeSinTitulo : null,
        agruparCabeceraYAvisoCentrados ? styles.yaVotoNoticeAgrupado : null,
        rellenarAlturaDisponible && !agruparCabeceraYAvisoCentrados
          ? styles.yaVotoNoticeRellenarCentro
          : null,
      ]}
    >
      <View style={[styles.yaVotoNoticeRecuadro, noticeSlot ? styles.yaVotoNoticeRecuadroSlot : null]}>
        {noticeSlot ?? (
          <>
            <Ionicons
              name="information-circle-outline"
              size={22}
              color="#ffffff"
              style={styles.yaVotoNoticeIcono}
            />
            <Text style={styles.yaVotoNoticeTextRegistrado}>{noticePlain}</Text>
          </>
        )}
      </View>
    </View>
  ) : null;

  const cabeceraCheckYTitulo = (
    <>
      {iconoCheckCabecera ? (
        <View style={styles.checkCabeceraWrap} accessibilityRole="image" accessibilityLabel="Voto registrado">
          <View style={styles.checkCabeceraCirculo}>
            <Ionicons name="checkmark" size={32} color="#ffffff" />
          </View>
        </View>
      ) : null}
      {showTitle ? (
        <Text style={[styles.successTitle, styles.successTitleYaRegistrado]}>{title.trim()}</Text>
      ) : null}
    </>
  );

  return (
    <View
      style={[
        styles.votacionSection,
        withSolapaFoto ? styles.votacionSectionSolapaFoto : null,
        styles.votacionSectionYaRegistrado,
        rellenarAlturaDisponible ? styles.votacionSectionRellenar : null,
      ]}
    >
      <View
        style={[styles.successContainer, rellenarAlturaDisponible ? styles.successContainerRellenar : null]}
      >
        {agruparCabeceraYAvisoCentrados ? (
          <View style={styles.cabeceraAvisoCentroWrap}>
            {cabeceraCheckYTitulo}
            {noticeRecuadro}
          </View>
        ) : (
          <>
            {cabeceraCheckYTitulo}
            {hasEncima ? <View style={styles.encimaRecuadroWrap}>{slotEncimaRecuadro}</View> : null}
            {noticeRecuadro}
          </>
        )}
        {showAction ? (
          <TouchableOpacity
            style={[
              styles.continuarButton,
              !showTitle && !hasNotice && !hasEncima ? styles.continuarButtonSolo : null,
              rellenarAlturaDisponible ? styles.continuarButtonAncladoAbajo : null,
              primaryActionDisabled ? styles.continuarButtonDisabled : null,
            ]}
            onPress={onPrimaryAction}
            activeOpacity={0.88}
            disabled={primaryActionDisabled}
          >
            <Text style={styles.continuarButtonText}>{primaryActionLabel}</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  votacionSection: {
    width: '100%',
    marginHorizontal: 0,
    marginBottom: 24,
    marginTop: 24,
    padding: 0,
    overflow: 'visible',
    backgroundColor: '#f9fafb',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  votacionSectionSolapaFoto: {
    marginTop: -14,
    zIndex: 1,
  },
  votacionSectionYaRegistrado: {
    backgroundColor: '#684535',
    borderColor: '#5a3a2d',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  votacionSectionRellenar: {
    flex: 1,
    marginBottom: 0,
    alignSelf: 'stretch',
  },
  successContainer: {
    alignItems: 'center',
    padding: 24,
    overflow: 'visible',
  },
  successContainerRellenar: {
    flex: 1,
    alignSelf: 'stretch',
    width: '100%',
    minHeight: 0,
  },
  checkCabeceraWrap: {
    marginBottom: 16,
    alignItems: 'center',
  },
  checkCabeceraCirculo: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#16a34a',
    borderWidth: 3,
    borderColor: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#030213',
    marginTop: 0,
    marginBottom: 8,
  },
  successTitleYaRegistrado: {
    color: '#ffffff',
    textAlign: 'center',
    alignSelf: 'stretch',
  },
  encimaRecuadroWrap: {
    alignSelf: 'stretch',
    alignItems: 'center',
    gap: 12,
    marginBottom: 4,
    overflow: 'visible',
  },
  yaVotoNoticeRegistrado: {
    marginTop: 12,
    alignSelf: 'stretch',
  },
  yaVotoNoticeSinTitulo: {
    marginTop: 0,
  },
  yaVotoNoticeRellenarCentro: {
    flex: 1,
    justifyContent: 'center',
    minHeight: 0,
  },
  cabeceraAvisoCentroWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    width: '100%',
    minHeight: 0,
  },
  yaVotoNoticeAgrupado: {
    marginTop: 12,
    alignSelf: 'stretch',
  },
  yaVotoNoticeRecuadro: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 22,
    borderWidth: 2,
    borderColor: '#3A1707',
    borderRadius: 12,
    backgroundColor: '#582A14',
  },
  yaVotoNoticeRecuadroSlot: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  yaVotoNoticeIcono: {
    flexShrink: 0,
  },
  yaVotoNoticeTextRegistrado: {
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    color: '#ffffff',
    lineHeight: 24,
    fontWeight: '500',
    textAlign: 'left',
  },
  continuarButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    alignSelf: 'stretch',
    marginTop: 24,
    backgroundColor: '#C50018',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
  },
  continuarButtonText: {
    flex: 1,
    width: '100%',
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  continuarButtonSolo: {
    marginTop: 0,
  },
  continuarButtonAncladoAbajo: {
    marginTop: 'auto',
  },
  continuarButtonDisabled: {
    opacity: 0.55,
  },
});
